import {
  generateEventHeaders,
  generateReportQueriesCsv,
  generateReportQueriesRequest,
  useReportQueriesFilters,
} from "./application/reportQueries.service";
import { useCallback, useMemo, useState } from "react";
import { format } from "date-fns";
import { currentDate } from "../../lib/dateUtils";
import CrudList, { defaultListMessages } from "../shared/crud/list";
import { reportQueriesMessages } from "./messages";
import { findEvents } from "./infrastructure/eventQueries.api";
import { useConfig } from "../../lib/config";
import SweetAlert from "sweetalert2";
import { useIntl } from "react-intl";
import { AxiosError } from "axios";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZES = [10, 25, 50, 100, 500];

export function ReportQueries() {
  const intl = useIntl();
  const config = useConfig();
  const filters = useReportQueriesFilters();
  const [totalResults, setTotalResults] = useState<number>(0);

  const headers = useMemo(() => generateEventHeaders(), []);

  const fetchQueriesReport = useCallback(
    async ({ currentPage, filterValues, pageSize, setTotalRowsCount }) => {
      try {
        const reportQueriesRequest = generateReportQueriesRequest({
          currentPage,
          filterValues,
          pageSize,
        });
        const response = await findEvents(config.eventsApiEndpoint, reportQueriesRequest);
        setTotalResults(response.total);
        if (typeof setTotalRowsCount === "function") {
          setTotalRowsCount(response.total);
        }

        return response.data;
      } catch (e) {
        let errorText = intl.formatMessage(reportQueriesMessages.errorGeneric);
        if (
          e instanceof AxiosError &&
          e.response?.data?.error.includes("The date range cannot exceed 31 days")
        ) {
          errorText = intl.formatMessage(reportQueriesMessages.errorExceed31Days);
        }

        await SweetAlert.fire({
          icon: "error",
          title: "Error",
          text: errorText,
        });
        return [];
      }
    },
    [config.eventsApiEndpoint, intl]
  );

  const onExport = useCallback(
    async ({ currentPage, filterValues, pageSize }) => {
      if (!totalResults) {
        return;
      }
      const results = await fetchQueriesReport({
        currentPage: 0,
        filterValues,
        pageSize: totalResults,
        setTotalRowsCount: undefined,
      });
      if (!results) {
        return;
      }

      const link = document.createElement("a");
      link.href = generateReportQueriesCsv(results);
      link.download = `queries_report_${format(currentDate(), "yyyyMMdd")}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [fetchQueriesReport, totalResults]
  );

  return (
    <CrudList
      messages={{
        ...defaultListMessages,
        entityName: reportQueriesMessages.title,
        tableHeaders: reportQueriesMessages,
      }}
      columns={headers}
      onGetAll={fetchQueriesReport}
      onExport={onExport}
      paging
      pageSizes={PAGE_SIZES}
      defaultPageSize={DEFAULT_PAGE_SIZE}
      filters={filters}
      managerName={"queriesReport"}
    />
  );
}
